import React, { useState, useEffect } from "react";
import { Icon } from "./icons.jsx";

/* Badge era el ultimo superviviente de src/components.jsx, la libreria de
   componentes del SVPT maqueta. Sus otros 12 exports murieron con las
   pantallas que los usaban (C2b-C2d), asi que vive aqui, donde se usa. */
function Badge({ tone = "", dot, children, style }) {
  return (
    <span className={"badge " + tone} style={style}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block", flexShrink: 0 }} />}
      {children}
    </span>
  );
}

/* ============================================================
   Talention — Notifications panel
   Slides in from the right when the bell is clicked
   ============================================================ */

const NOTIFICATIONS_SEED = [
  {
    id: 1,
    kind: "warn",
    icon: "alert",
    title: "Pesos de factores desbalanceados",
    body: "Los pesos SVPT actuales no suman 100%. Las valoraciones pueden no ser fiables.",
    time: "hace 4 min",
    unread: true,
    action: { label: "Ir a factores", to: "/svpt/factors" },
  },
  {
    id: 2,
    kind: "info",
    icon: "play",
    title: "Valoración guardada como borrador",
    body: "Director/a Comercial · 6 de 10 factores completados",
    time: "hace 1 h",
    unread: true,
    action: { label: "Continuar", to: "/svpt/valuation" },
  },
  {
    id: 3,
    kind: "good",
    icon: "circle-check",
    title: "Valoración finalizada",
    body: "Director/a Recursos Humanos asignado a Escala 04 con 585 puntos",
    time: "hace 2 h",
    unread: true,
    action: { label: "Ver", to: "/svpt/valuation" },
  },
  {
    id: 4,
    kind: "info",
    icon: "plus",
    title: "Nuevo puesto registrado",
    body: "Conductor/a · Departamento de Logística",
    time: "ayer",
    unread: false,
  },
  {
    id: 5,
    kind: "info",
    icon: "users",
    title: "Departamento actualizado",
    body: "Recursos Humanos · descripción modificada",
    time: "ayer",
    unread: false,
  },
  {
    id: 6,
    kind: "good",
    icon: "shield",
    title: "Backup completado",
    body: "Snapshot diario almacenado en EU-West-2",
    time: "hace 2 días",
    unread: false,
  },
];

function NotificationButton() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(NOTIFICATIONS_SEED);
  const unreadCount = list.filter(n => n.unread).length;

  const markRead = (id) => setList(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));
  const markAllRead = () => setList(prev => prev.map(n => ({ ...n, unread: false })));
  const remove = (id) => setList(prev => prev.filter(n => n.id !== id));
  const clearAll = () => setList([]);

  return (
    <>
      <button className="icon-btn" onClick={() => setOpen(true)} style={{ position: "relative" }} title="Notificaciones">
        <Icon name="bell" size={15} />
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 4,
            width: 8, height: 8, borderRadius: "50%",
            background: "var(--danger)", border: "1.5px solid var(--white)",
          }} />
        )}
      </button>

      {open && <NotificationPanel
        list={list}
        unreadCount={unreadCount}
        onClose={() => setOpen(false)}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onRemove={remove}
        onClearAll={clearAll}
      />}
    </>
  );
}

function NotificationPanel({ list, unreadCount, onClose, onMarkRead, onMarkAllRead, onRemove, onClearAll }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const grouped = list.reduce((acc, n) => {
    const key = n.time.includes("min") || n.time.includes("h") ? "Hoy" : n.time.includes("ayer") ? "Ayer" : "Antes";
    (acc[key] = acc[key] || []).push(n);
    return acc;
  }, {});

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 90,
          background: "rgba(15,18,26,0.20)",
          animation: "fadeBg 200ms ease both",
        }}
      />

      {/* Panel */}
      <div
        className="notif-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: 14, right: 14, bottom: 14,
          width: 380,
          zIndex: 100,
          background: "var(--surface-card)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-default)",
          boxShadow: "var(--shadow-lg)",
          display: "flex", flexDirection: "column",
          animation: "slideInRight 280ms cubic-bezier(.2,.7,.2,1) both",
          overflow: "hidden",
        }}>
        {/* Header */}
        <div style={{
          padding: "20px 22px 16px",
          display: "flex", flexDirection: "column", gap: 14,
          borderBottom: "1px solid var(--border)",
        }}>
          <div className="spread">
            <div>
              <div className="card-eyebrow">Centro de actividad</div>
              <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--weight-bold)", letterSpacing: "-0.02em", marginTop: 4 }}>
                Notificaciones
              </div>
            </div>
            <button className="icon-btn" onClick={onClose} style={{ width: 30, height: 30 }}>
              <Icon name="x" size={14} />
            </button>
          </div>
          <div className="spread">
            <div className="row" style={{ gap: 8 }}>
              {unreadCount > 0 ? (
                <Badge tone="bad" dot>{unreadCount} sin leer</Badge>
              ) : (
                <Badge tone="good" dot>Todo al día</Badge>
              )}
            </div>
            {list.length > 0 && (
              <div className="row" style={{ gap: 8 }}>
                {unreadCount > 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={onMarkAllRead}>
                    Marcar todo
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={onClearAll}>
                  Limpiar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 12px 20px" }}>
          {list.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--ink-500)" }}>
              <div style={{
                width: 52, height: 52, borderRadius: 16,
                background: "rgba(255,255,255,0.7)", border: "1px solid var(--border)",
                display: "grid", placeItems: "center", margin: "0 auto 12px",
              }}>
                <Icon name="bell" size={22} className="muted" />
              </div>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)" }}>Sin notificaciones</div>
              <div className="tiny muted" style={{ marginTop: 4 }}>Te avisaremos cuando algo requiera tu atención.</div>
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} style={{ marginBottom: 12 }}>
                <div style={{
                  fontSize: "var(--text-xs)", fontWeight: "var(--weight-bold)", color: "var(--ink-500)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  padding: "10px 8px 6px",
                }}>{group}</div>
                <div className="stack" style={{ gap: 4 }}>
                  {items.map((n, i) => <NotifItem key={n.id} notif={n} onRead={onMarkRead} onRemove={onRemove} index={i} />)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 18px",
          borderTop: "1px solid var(--border)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "rgba(255,255,255,0.4)",
        }}>
          <span className="tiny muted">Notificaciones de los últimos 7 días</span>
          <button className="btn btn-ghost btn-sm">
            <Icon name="settings" size={12} /> Ajustes
          </button>
        </div>
      </div>
    </>
  );
}

function NotifItem({ notif, onRead, onRemove, index }) {
  const toneColor = {
    warn: { bg: "var(--warn-100)", fg: "var(--warning-700)" },
    bad:  { bg: "var(--bad-100)", fg: "var(--danger-700)" },
    good: { bg: "var(--good-100)", fg: "var(--success-700)" },
    info: { bg: "var(--info-100)", fg: "var(--info-700)" },
  }[notif.kind] || { bg: "var(--ink-150)", fg: "var(--ink-700)" };

  return (
    <div
      style={{
        background: notif.unread ? "var(--surface-card)" : "rgba(255,255,255,0.5)",
        border: "1px solid " + (notif.unread ? "var(--border-strong)" : "var(--border)"),
        borderRadius: 14,
        padding: "12px 14px",
        display: "flex", gap: 10, alignItems: "flex-start",
        cursor: "pointer",
        animation: `rise 280ms cubic-bezier(.2,.7,.2,1) both`,
        animationDelay: (index * 40) + "ms",
        position: "relative",
        transition: "background 120ms",
      }}
      onClick={() => {
        if (notif.unread) onRead(notif.id);
        if (notif.action?.to) window.GO(notif.action.to);
      }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--surface-card)"}
      onMouseLeave={e => e.currentTarget.style.background = notif.unread ? "var(--surface-card)" : "rgba(255,255,255,0.5)"}
    >
      {notif.unread && (
        <span style={{
          position: "absolute", top: 14, right: 14,
          width: 8, height: 8, borderRadius: "50%",
          background: "var(--info)",
        }} />
      )}
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: toneColor.bg, color: toneColor.fg,
        display: "grid", placeItems: "center", flexShrink: 0,
      }}><Icon name={notif.icon} size={15} /></div>
      <div style={{ flex: 1, minWidth: 0, paddingRight: notif.unread ? 14 : 0 }}>
        <div className="semi" style={{ fontSize: "var(--text-sm)", letterSpacing: "-0.01em", lineHeight: 1.3 }}>
          {notif.title}
        </div>
        <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 3, lineHeight: 1.45 }}>
          {notif.body}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
          <span className="tiny muted">{notif.time}</span>
          {notif.action && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--ink-900)", fontWeight: "var(--weight-semibold)" }}>
              {notif.action.label} <Icon name="arrow-right" size={11} style={{ verticalAlign: "-1px" }} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export { NotificationButton };